import axios, { AxiosInstance, AxiosError } from 'axios';
import { toast } from 'react-hot-toast';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888';
const WS_BASE_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8888';

// Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse>) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
      toast.error('Session expired. Please login again.');
    } else if (error.response?.data?.error) {
      toast.error(error.response.data.error);
    } else if (error.message === 'Network Error') {
      toast.error('Network error. Please check your connection.');
    } else {
      toast.error('An unexpected error occurred');
    }
    return Promise.reject(error);
  }
);

// Auth Services
export const authService = {
  login: async (googleToken: string): Promise<ApiResponse<{ token: string; user: any }>> => {
    const response = await api.post('/auth/google', { token: googleToken });
    return response.data;
  },

  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
    localStorage.removeItem('auth_token');
  },

  getCurrentUser: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  refreshToken: async (): Promise<ApiResponse<{ token: string }>> => {
    const response = await api.post('/auth/refresh');
    return response.data;
  },
};

// Portal Services
export const portalService = {
  // Admin Portal
  admin: {
    getDashboard: async (): Promise<ApiResponse<any>> => {
      const response = await api.get('/portal/admin/dashboard');
      return response.data;
    },

    getUsers: async (page = 1, pageSize = 20): Promise<ApiResponse<PaginatedResponse<any>>> => {
      const response = await api.get('/portal/admin/users', { params: { page, pageSize } });
      return response.data;
    },

    createUser: async (userData: any): Promise<ApiResponse<any>> => {
      const response = await api.post('/portal/admin/users', userData);
      return response.data;
    },

    updateUser: async (userId: string, userData: any): Promise<ApiResponse<any>> => {
      const response = await api.put(`/portal/admin/users/${userId}`, userData);
      return response.data;
    },

    deleteUser: async (userId: string): Promise<ApiResponse<void>> => {
      const response = await api.delete(`/portal/admin/users/${userId}`);
      return response.data;
    },

    getStores: async (): Promise<ApiResponse<any[]>> => {
      const response = await api.get('/portal/admin/stores');
      return response.data;
    },

    updateStore: async (storeId: string, storeData: any): Promise<ApiResponse<any>> => {
      const response = await api.put(`/portal/admin/stores/${storeId}`, storeData);
      return response.data;
    },
  },

  // Store Manager Portal
  manager: {
    getDashboard: async (storeId: string): Promise<ApiResponse<any>> => {
      const response = await api.get(`/portal/manager/dashboard/${storeId}`);
      return response.data;
    },

    submitPayroll: async (payrollData: any): Promise<ApiResponse<any>> => {
      const response = await api.post('/portal/manager/payroll', payrollData);
      return response.data;
    },

    getPayrollHistory: async (storeId: string, startDate?: string, endDate?: string): Promise<ApiResponse<any[]>> => {
      const response = await api.get(`/portal/manager/payroll/${storeId}`, {
        params: { startDate, endDate },
      });
      return response.data;
    },

    getSchedule: async (storeId: string, week?: string): Promise<ApiResponse<any>> => {
      const response = await api.get(`/portal/manager/schedule/${storeId}`, { params: { week } });
      return response.data;
    },

    updateSchedule: async (scheduleData: any): Promise<ApiResponse<any>> => {
      const response = await api.put('/portal/manager/schedule', scheduleData);
      return response.data;
    },
  },

  // Franchisee Portal
  franchisee: {
    getOverview: async (): Promise<ApiResponse<any>> => {
      const response = await api.get('/portal/franchisee/overview');
      return response.data;
    },

    getRevenueLandscape: async (timeRange = '30d'): Promise<ApiResponse<any>> => {
      const response = await api.get('/portal/franchisee/revenue-landscape', {
        params: { timeRange },
      });
      return response.data;
    },

    getPredictiveAnalytics: async (): Promise<ApiResponse<any>> => {
      const response = await api.get('/analytics/predictive');
      return response.data;
    },

    getStrategicInsights: async (): Promise<ApiResponse<any>> => {
      const response = await api.get('/portal/franchisee/insights');
      return response.data;
    },
  },
};

// Analytics Services
export const analyticsService = {
  getKPIs: async (storeId?: string, metrics?: string[]): Promise<ApiResponse<any>> => {
    const response = await api.get('/analytics/kpis', {
      params: { storeId, metrics: metrics?.join(',') },
    });
    return response.data;
  },

  queryKPIs: async (query: string): Promise<ApiResponse<any>> => {
    const response = await api.post('/analytics/kpis/query', { query });
    return response.data;
  },

  getTrends: async (metric: string, storeId?: string, period = '30d'): Promise<ApiResponse<any>> => {
    const response = await api.get('/analytics/trends', {
      params: { metric, storeId, period },
    });
    return response.data;
  },

  getComparisons: async (storeIds: string[], metrics: string[]): Promise<ApiResponse<any>> => {
    const response = await api.post('/analytics/compare', { storeIds, metrics });
    return response.data;
  },

  exportReport: async (reportType: string, filters: any): Promise<Blob> => {
    const response = await api.post(
      '/analytics/export',
      { reportType, filters },
      { responseType: 'blob' }
    );
    return response.data;
  },
};

// PDF Processing Services
export const pdfService = {
  uploadInvoices: async (files: File[], storeId: string): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('storeId', storeId);

    const response = await api.post('/pdf/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total || 1)
        );
        // You can dispatch progress to a store here
        console.log(`Upload Progress: ${percentCompleted}%`);
      },
    });
    return response.data;
  },

  getProcessingStatus: async (jobId: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/pdf/status/${jobId}`);
    return response.data;
  },

  getProcessedInvoices: async (
    storeId: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<any[]>> => {
    const response = await api.get(`/pdf/invoices/${storeId}`, {
      params: { startDate, endDate },
    });
    return response.data;
  },

  reprocessInvoice: async (invoiceId: string): Promise<ApiResponse<any>> => {
    const response = await api.post(`/pdf/reprocess/${invoiceId}`);
    return response.data;
  },
};

// Voice Services
export const voiceService = {
  processCommand: async (audioBlob: Blob): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'command.webm');

    const response = await api.post('/voice/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  textToSpeech: async (text: string): Promise<Blob> => {
    const response = await api.post(
      '/voice/tts',
      { text },
      { responseType: 'blob' }
    );
    return response.data;
  },

  getVoiceCommands: async (): Promise<ApiResponse<string[]>> => {
    const response = await api.get('/voice/commands');
    return response.data;
  },
};

// Notification Services
export const notificationService = {
  getNotifications: async (unreadOnly = false): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/notifications', {
      params: { unreadOnly },
    });
    return response.data;
  },

  markAsRead: async (notificationId: string): Promise<ApiResponse<void>> => {
    const response = await api.put(`/notifications/${notificationId}/read`);
    return response.data;
  },

  markAllAsRead: async (): Promise<ApiResponse<void>> => {
    const response = await api.put('/notifications/read-all');
    return response.data;
  },

  updatePreferences: async (preferences: any): Promise<ApiResponse<any>> => {
    const response = await api.put('/notifications/preferences', preferences);
    return response.data;
  },
};

// TekMetric Integration (Legacy - kept for backward compatibility)
export const tekmetricLegacyService = {
  syncData: async (storeId: string): Promise<ApiResponse<any>> => {
    const response = await api.post(`/integrations/tekmetric/sync/${storeId}`);
    return response.data;
  },

  getConnectionStatus: async (storeId: string): Promise<ApiResponse<any>> => {
    const response = await api.get(`/integrations/tekmetric/status/${storeId}`);
    return response.data;
  },

  configureWebhook: async (storeId: string, webhookUrl: string): Promise<ApiResponse<any>> => {
    const response = await api.post(`/integrations/tekmetric/webhook`, {
      storeId,
      webhookUrl,
    });
    return response.data;
  },
};

// Financial Data Services (CBA Integration)
export const financialService = {
  getSummary: async (): Promise<ApiResponse<any[]>> => {
    const response = await api.get('/api/v1/financial/summary');
    return { success: true, data: response.data };
  },

  getLocationPerformance: async (location: string, months = 6): Promise<ApiResponse<any>> => {
    const response = await api.get(`/api/v1/financial/performance/${location}`, {
      params: { months },
    });
    return { success: true, data: response.data };
  },

  getCombinedMetrics: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/api/v1/financial/combined');
    return { success: true, data: response.data };
  },
};

// System Services
export const systemService = {
  getRustStatus: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/api/v1/system/rust-status');
    return { success: true, data: response.data };
  },

  getStats: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/api/system/stats');
    return response.data;
  },
};

// TekMetric Services
export const tekmetricService = {
  getStatus: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/api/v1/tekmetric/status');
    return { success: true, data: response.data };
  },

  getData: async (location?: string): Promise<ApiResponse<any>> => {
    const url = location
      ? `/api/v1/tekmetric/data?location=${location}`
      : '/api/v1/tekmetric/data';
    const response = await api.get(url);
    return { success: true, data: response.data };
  },

  triggerSync: async (location?: string, force = false): Promise<ApiResponse<any>> => {
    const params = new URLSearchParams();
    if (location) params.append('location', location);
    if (force) params.append('force', 'true');
    const url = `/api/v1/tekmetric/sync${params.toString() ? '?' + params.toString() : ''}`;
    const response = await api.post(url);
    return { success: true, data: response.data };
  },

  getLocations: async (): Promise<ApiResponse<any>> => {
    const response = await api.get('/api/v1/tekmetric/locations');
    return { success: true, data: response.data };
  },
};

// Export default api instance
export default api;